import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, RefreshCw, Save, X, Power, PowerOff, Luggage } from 'lucide-react';
import { baggageRuleService } from '../services/baggageRuleService';
import { BaggageRule, CreateBaggageRuleRequest } from '../interfaces/BaggageRule';
import { DefaultLoader } from '../components';

const EMPTY_FORM: CreateBaggageRuleRequest = {
  airlineCode: '',
  airlineName: '',
  weightKg: 23,
  hoursBeforeDeparture: null,
  price: 0,
  isActive: true,
};

const BaggageRules: React.FC = () => {
  const [rules, setRules] = useState<BaggageRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingRule, setEditingRule] = useState<BaggageRule | null>(null);
  const [formData, setFormData] = useState<CreateBaggageRuleRequest>(EMPTY_FORM);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadRules();
  }, []);

  const loadRules = async () => {
    try {
      setLoading(true);
      const data = await baggageRuleService.getAll();
      setRules(data);
    } catch {
      setError('Erro ao carregar regras de bagagem.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSaving(true);
    try {
      const payload: CreateBaggageRuleRequest = {
        ...formData,
        airlineCode: formData.airlineCode.toUpperCase().trim(),
        hoursBeforeDeparture:
          formData.hoursBeforeDeparture === null ||
          formData.hoursBeforeDeparture === undefined ||
          String(formData.hoursBeforeDeparture) === ''
            ? null
            : Number(formData.hoursBeforeDeparture),
        price: Number(formData.price),
        weightKg: Number(formData.weightKg) || 23,
      };

      if (editingRule) {
        await baggageRuleService.update(editingRule.id, payload);
      } else {
        await baggageRuleService.create(payload);
      }
      handleCancelForm();
      await loadRules();
    } catch {
      setError('Erro ao salvar regra de bagagem.');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (rule: BaggageRule) => {
    setEditingRule(rule);
    setFormData({
      airlineCode: rule.airlineCode,
      airlineName: rule.airlineName,
      weightKg: rule.weightKg,
      hoursBeforeDeparture: rule.hoursBeforeDeparture,
      price: rule.price,
      isActive: rule.isActive,
    });
    setShowForm(true);
    setError(null);
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Tem certeza que deseja excluir esta regra?')) return;
    try {
      await baggageRuleService.delete(id);
      await loadRules();
    } catch {
      setError('Erro ao excluir regra.');
    }
  };

  const handleToggleActive = async (rule: BaggageRule) => {
    try {
      await baggageRuleService.update(rule.id, { isActive: !rule.isActive });
      await loadRules();
    } catch {
      setError('Erro ao atualizar status da regra.');
    }
  };

  const handleCancelForm = () => {
    setShowForm(false);
    setEditingRule(null);
    setFormData(EMPTY_FORM);
    setError(null);
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value, type } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]:
        type === 'checkbox'
          ? (e.target as HTMLInputElement).checked
          : value === ''
          ? name === 'hoursBeforeDeparture'
            ? null
            : value
          : value,
    }));
  };

  // Agrupa as regras por companhia para exibição organizada
  const groupedRules = rules.reduce<Record<string, BaggageRule[]>>(
    (acc, rule) => {
      const key = `${rule.airlineCode} — ${rule.airlineName}`;
      if (!acc[key]) acc[key] = [];
      acc[key].push(rule);
      return acc;
    },
    {},
  );

  const formatHours = (hours: number | null): string => {
    if (hours === null) return 'Padrão (sem limite)';
    if (hours < 24) return `${hours}h antes`;
    return `${hours / 24}d (${hours}h) antes`;
  };

  const formatPrice = (price: number) =>
    Number(price).toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    });

  if (loading) return <DefaultLoader />;

  return (
    <div style={{ padding: '24px', maxWidth: '960px' }}>
      {/* Cabeçalho */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '24px',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <Luggage size={28} color="#1890ff" />
          <div>
            <h1 style={{ margin: 0, fontSize: '22px', fontWeight: 700 }}>
              Regras de Bagagem
            </h1>
            <p style={{ margin: 0, color: '#888', fontSize: '13px' }}>
              Configure os preços de bagagem adicional 23kg por companhia e
              antecedência de embarque
            </p>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            onClick={loadRules}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '8px 14px',
              border: '1px solid #d9d9d9',
              borderRadius: '6px',
              background: '#fff',
              cursor: 'pointer',
              fontSize: '13px',
            }}
          >
            <RefreshCw size={14} />
            Atualizar
          </button>
          <button
            onClick={() => {
              setShowForm(true);
              setEditingRule(null);
              setFormData(EMPTY_FORM);
              setError(null);
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '8px 16px',
              background: '#1890ff',
              color: '#fff',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: 600,
              fontSize: '13px',
            }}
          >
            <Plus size={16} />
            Nova Regra
          </button>
        </div>
      </div>

      {/* Mensagem de erro */}
      {error && (
        <div
          style={{
            background: '#fff2f0',
            border: '1px solid #ffccc7',
            borderRadius: '6px',
            padding: '12px 16px',
            marginBottom: '16px',
            color: '#a8071a',
            fontSize: '13px',
          }}
        >
          {error}
        </div>
      )}

      {/* Formulário */}
      {showForm && (
        <div
          style={{
            background: '#f8f9fa',
            border: '1px solid #e8e8e8',
            borderRadius: '8px',
            padding: '20px',
            marginBottom: '24px',
          }}
        >
          <h3 style={{ margin: '0 0 16px', fontSize: '15px', fontWeight: 600 }}>
            {editingRule ? 'Editar Regra' : 'Nova Regra de Bagagem'}
          </h3>
          <form onSubmit={handleSubmit}>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 2fr',
                gap: '12px',
                marginBottom: '12px',
              }}
            >
              <div>
                <label
                  style={{
                    display: 'block',
                    fontSize: '12px',
                    fontWeight: 600,
                    marginBottom: '4px',
                    color: '#555',
                  }}
                >
                  Código IATA *
                </label>
                <input
                  name="airlineCode"
                  value={formData.airlineCode}
                  onChange={handleChange}
                  required
                  maxLength={10}
                  placeholder="ex: AD, G3, LA"
                  style={{
                    width: '100%',
                    padding: '8px 10px',
                    border: '1px solid #d9d9d9',
                    borderRadius: '6px',
                    fontSize: '13px',
                    boxSizing: 'border-box',
                  }}
                />
              </div>
              <div>
                <label
                  style={{
                    display: 'block',
                    fontSize: '12px',
                    fontWeight: 600,
                    marginBottom: '4px',
                    color: '#555',
                  }}
                >
                  Nome da Companhia *
                </label>
                <input
                  name="airlineName"
                  value={formData.airlineName}
                  onChange={handleChange}
                  required
                  maxLength={100}
                  placeholder="ex: Azul Linhas Aéreas, GOL, LATAM"
                  style={{
                    width: '100%',
                    padding: '8px 10px',
                    border: '1px solid #d9d9d9',
                    borderRadius: '6px',
                    fontSize: '13px',
                    boxSizing: 'border-box',
                  }}
                />
              </div>
            </div>

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr 1fr',
                gap: '12px',
                marginBottom: '12px',
              }}
            >
              <div>
                <label
                  style={{
                    display: 'block',
                    fontSize: '12px',
                    fontWeight: 600,
                    marginBottom: '4px',
                    color: '#555',
                  }}
                >
                  Peso (kg)
                </label>
                <input
                  name="weightKg"
                  type="number"
                  min={1}
                  value={formData.weightKg}
                  onChange={handleChange}
                  style={{
                    width: '100%',
                    padding: '8px 10px',
                    border: '1px solid #d9d9d9',
                    borderRadius: '6px',
                    fontSize: '13px',
                    boxSizing: 'border-box',
                  }}
                />
              </div>
              <div>
                <label
                  style={{
                    display: 'block',
                    fontSize: '12px',
                    fontWeight: 600,
                    marginBottom: '4px',
                    color: '#555',
                  }}
                >
                  Horas antes do embarque
                  <span
                    style={{
                      fontWeight: 400,
                      color: '#888',
                      marginLeft: '4px',
                    }}
                  >
                    (vazio = padrão)
                  </span>
                </label>
                <input
                  name="hoursBeforeDeparture"
                  type="number"
                  min={1}
                  value={
                    formData.hoursBeforeDeparture === null ||
                    formData.hoursBeforeDeparture === undefined
                      ? ''
                      : formData.hoursBeforeDeparture
                  }
                  onChange={handleChange}
                  placeholder="ex: 48"
                  style={{
                    width: '100%',
                    padding: '8px 10px',
                    border: '1px solid #d9d9d9',
                    borderRadius: '6px',
                    fontSize: '13px',
                    boxSizing: 'border-box',
                  }}
                />
              </div>
              <div>
                <label
                  style={{
                    display: 'block',
                    fontSize: '12px',
                    fontWeight: 600,
                    marginBottom: '4px',
                    color: '#555',
                  }}
                >
                  Preço (R$) *
                </label>
                <input
                  name="price"
                  type="number"
                  min={0}
                  step="0.01"
                  value={formData.price}
                  onChange={handleChange}
                  required
                  placeholder="ex: 189.90"
                  style={{
                    width: '100%',
                    padding: '8px 10px',
                    border: '1px solid #d9d9d9',
                    borderRadius: '6px',
                    fontSize: '13px',
                    boxSizing: 'border-box',
                  }}
                />
              </div>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontSize: '13px',
                  cursor: 'pointer',
                }}
              >
                <input
                  name="isActive"
                  type="checkbox"
                  checked={formData.isActive ?? true}
                  onChange={handleChange}
                />
                Regra ativa
              </label>
            </div>

            <div
              style={{
                background: '#e6f7ff',
                border: '1px solid #91d5ff',
                borderRadius: '6px',
                padding: '10px 14px',
                marginBottom: '16px',
                fontSize: '12px',
                color: '#0050b3',
              }}
            >
              <strong>Como funciona a seleção de regra:</strong> quando o cliente
              vai ao checkout, o sistema calcula quantas horas faltam para o
              embarque e aplica a regra com o menor "Horas antes" que ainda cubra
              esse tempo. Se nenhuma cobrir, usa a regra padrão (campo vazio).
              <br />
              <strong>Exemplo Azul:</strong> Padrão = R$ 189,90 · 48h = R$ 230,00
              · 24h = R$ 289,90
            </div>

            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                type="submit"
                disabled={saving}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '8px 18px',
                  background: saving ? '#ccc' : '#52c41a',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: saving ? 'not-allowed' : 'pointer',
                  fontWeight: 600,
                  fontSize: '13px',
                }}
              >
                <Save size={14} />
                {saving ? 'Salvando…' : 'Salvar'}
              </button>
              <button
                type="button"
                onClick={handleCancelForm}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '8px 14px',
                  border: '1px solid #d9d9d9',
                  borderRadius: '6px',
                  background: '#fff',
                  cursor: 'pointer',
                  fontSize: '13px',
                }}
              >
                <X size={14} />
                Cancelar
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Lista agrupada por companhia */}
      {Object.keys(groupedRules).length === 0 ? (
        <div
          style={{
            textAlign: 'center',
            padding: '48px',
            color: '#888',
            border: '1px dashed #d9d9d9',
            borderRadius: '8px',
          }}
        >
          <Luggage size={40} color="#ccc" style={{ marginBottom: '12px' }} />
          <p style={{ margin: 0 }}>Nenhuma regra de bagagem cadastrada.</p>
          <p style={{ margin: '4px 0 0', fontSize: '13px' }}>
            Clique em "Nova Regra" para começar.
          </p>
        </div>
      ) : (
        Object.entries(groupedRules).map(([airlineLabel, airlineRules]) => (
          <div
            key={airlineLabel}
            style={{
              background: '#fff',
              border: '1px solid #e8e8e8',
              borderRadius: '8px',
              marginBottom: '16px',
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                padding: '12px 16px',
                background: '#fafafa',
                borderBottom: '1px solid #e8e8e8',
                fontWeight: 700,
                fontSize: '14px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              <Luggage size={16} color="#1890ff" />
              {airlineLabel}
              <span
                style={{
                  marginLeft: '8px',
                  background: '#e6f7ff',
                  color: '#1890ff',
                  padding: '2px 8px',
                  borderRadius: '10px',
                  fontSize: '11px',
                  fontWeight: 500,
                }}
              >
                {airlineRules.length} regra{airlineRules.length !== 1 ? 's' : ''}
              </span>
            </div>

            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#fafafa', fontSize: '12px' }}>
                  <th
                    style={{
                      padding: '8px 16px',
                      textAlign: 'left',
                      fontWeight: 600,
                      color: '#666',
                      borderBottom: '1px solid #f0f0f0',
                    }}
                  >
                    Antecedência
                  </th>
                  <th
                    style={{
                      padding: '8px 16px',
                      textAlign: 'left',
                      fontWeight: 600,
                      color: '#666',
                      borderBottom: '1px solid #f0f0f0',
                    }}
                  >
                    Peso
                  </th>
                  <th
                    style={{
                      padding: '8px 16px',
                      textAlign: 'left',
                      fontWeight: 600,
                      color: '#666',
                      borderBottom: '1px solid #f0f0f0',
                    }}
                  >
                    Preço
                  </th>
                  <th
                    style={{
                      padding: '8px 16px',
                      textAlign: 'left',
                      fontWeight: 600,
                      color: '#666',
                      borderBottom: '1px solid #f0f0f0',
                    }}
                  >
                    Status
                  </th>
                  <th
                    style={{
                      padding: '8px 16px',
                      textAlign: 'right',
                      fontWeight: 600,
                      color: '#666',
                      borderBottom: '1px solid #f0f0f0',
                    }}
                  >
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody>
                {airlineRules
                  .sort((a, b) => {
                    if (a.hoursBeforeDeparture === null) return 1;
                    if (b.hoursBeforeDeparture === null) return -1;
                    return a.hoursBeforeDeparture - b.hoursBeforeDeparture;
                  })
                  .map((rule) => (
                    <tr
                      key={rule.id}
                      style={{
                        fontSize: '13px',
                        opacity: rule.isActive ? 1 : 0.5,
                        borderBottom: '1px solid #f0f0f0',
                      }}
                    >
                      <td style={{ padding: '10px 16px' }}>
                        <span
                          style={{
                            background:
                              rule.hoursBeforeDeparture === null
                                ? '#f6ffed'
                                : '#fff7e6',
                            color:
                              rule.hoursBeforeDeparture === null
                                ? '#389e0d'
                                : '#d46b08',
                            padding: '3px 10px',
                            borderRadius: '10px',
                            fontSize: '12px',
                            fontWeight: 500,
                          }}
                        >
                          {formatHours(rule.hoursBeforeDeparture)}
                        </span>
                      </td>
                      <td style={{ padding: '10px 16px' }}>
                        {rule.weightKg}kg
                      </td>
                      <td
                        style={{
                          padding: '10px 16px',
                          fontWeight: 700,
                          color: '#389e0d',
                        }}
                      >
                        {formatPrice(rule.price)}
                      </td>
                      <td style={{ padding: '10px 16px' }}>
                        <span
                          style={{
                            background: rule.isActive ? '#f6ffed' : '#f5f5f5',
                            color: rule.isActive ? '#389e0d' : '#999',
                            padding: '2px 8px',
                            borderRadius: '10px',
                            fontSize: '11px',
                            fontWeight: 500,
                          }}
                        >
                          {rule.isActive ? 'Ativa' : 'Inativa'}
                        </span>
                      </td>
                      <td style={{ padding: '10px 16px', textAlign: 'right' }}>
                        <div
                          style={{
                            display: 'flex',
                            gap: '6px',
                            justifyContent: 'flex-end',
                          }}
                        >
                          <button
                            title={
                              rule.isActive
                                ? 'Desativar regra'
                                : 'Ativar regra'
                            }
                            onClick={() => handleToggleActive(rule)}
                            style={{
                              padding: '5px',
                              border: '1px solid #d9d9d9',
                              borderRadius: '4px',
                              background: '#fff',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                            }}
                          >
                            {rule.isActive ? (
                              <Power size={13} color="#389e0d" />
                            ) : (
                              <PowerOff size={13} color="#999" />
                            )}
                          </button>
                          <button
                            title="Editar"
                            onClick={() => handleEdit(rule)}
                            style={{
                              padding: '5px',
                              border: '1px solid #d9d9d9',
                              borderRadius: '4px',
                              background: '#fff',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                            }}
                          >
                            <Edit2 size={13} color="#1890ff" />
                          </button>
                          <button
                            title="Excluir"
                            onClick={() => handleDelete(rule.id)}
                            style={{
                              padding: '5px',
                              border: '1px solid #ffccc7',
                              borderRadius: '4px',
                              background: '#fff',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                            }}
                          >
                            <Trash2 size={13} color="#cf1322" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        ))
      )}
    </div>
  );
};

export default BaggageRules;
